import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { Dialog, DialogPortal, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CEIC, CEIC_DISMISSAL_KEY, CEIC_DISMISSAL_MS, shouldShowCeicAnnouncement } from "@/lib/ceic";

let memoryDismissedUntil = 0;

export function CeicAnnouncement() {
  const [open, setOpen] = useState(false);
  const previousFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    let dismissedUntil = String(memoryDismissedUntil);
    for (const storage of ["localStorage", "sessionStorage"] as const) {
      try {
        const stored = window[storage].getItem(CEIC_DISMISSAL_KEY);
        if (Number(stored) > Number(dismissedUntil)) dismissedUntil = stored ?? dismissedUntil;
      } catch { /* In-memory dismissal remains available when storage is blocked. */ }
    }
    if (!shouldShowCeicAnnouncement(dismissedUntil, Date.now())) return;
    const timer = window.setTimeout(() => {
      // Do not interrupt typing, another dialog, or a visitor who has left home.
      if (window.location.pathname !== "/" || document.querySelector('[role="dialog"]') ||
          document.activeElement?.matches("input, textarea, select")) return;
      previousFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setOpen(true);
    }, 3500);
    return () => window.clearTimeout(timer);
  }, []);

  function dismiss() {
    memoryDismissedUntil = Date.now() + CEIC_DISMISSAL_MS;
    for (const storage of ["localStorage", "sessionStorage"] as const) {
      try { window[storage].setItem(CEIC_DISMISSAL_KEY, String(memoryDismissedUntil)); } catch { /* Best effort. */ }
    }
    setOpen(false);
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) dismiss(); }}>
      <DialogPortal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-ink/80" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-xl max-h-[calc(100dvh-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-sm border border-gold/50 bg-navy p-6 text-warm-white sm:p-10"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (previousFocus.current?.isConnected && previousFocus.current !== document.body) previousFocus.current.focus({ preventScroll: true });
            else document.getElementById("main")?.focus({ preventScroll: true });
          }}
        >
          <DialogPrimitive.Close asChild>
            <Button variant="ghost" size="icon" aria-label="Close conference announcement" className="absolute right-3 top-3 min-h-11 min-w-11 text-warm-white"><X aria-hidden /></Button>
          </DialogPrimitive.Close>
          <p className="eyebrow mb-6 pr-10">{CEIC.edition}</p>
          <DialogTitle className="font-display text-3xl leading-tight tracking-normal sm:text-4xl">{CEIC.name}</DialogTitle>
          <p className="mt-5 font-semibold text-gold">{CEIC.dates} · {CEIC.format}</p>
          <p className="mt-6 font-display text-2xl italic text-warm-white">{CEIC.theme}</p>
          <DialogDescription className="mt-4 text-base leading-relaxed text-warm-white/85">A national conference moving Community Engagement from activity to measurable impact. Limited to {CEIC.capacity} attendees. Registration is not yet open.</DialogDescription>
          <Button asChild className="btn-base btn-primary mt-8 h-auto w-full whitespace-normal text-center"><Link to="/conference" onClick={dismiss}>Explore the Conference</Link></Button>
          <a href={CEIC.url} target="_blank" rel="noopener noreferrer" onClick={dismiss} className="mt-4 flex min-h-11 items-center justify-center text-center text-sm text-gold underline underline-offset-4">Visit the official CEIC site</a>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}